import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { destroySession } from '../../helpers/Session';
import axios from '../../api/axios';
import DataTable from 'react-data-table-component';
import UserNDOTLoader from '../../components/loader/UserNDOTLoader';
import toast from '../../components/sweetalert/Sweetalert';
import Swal from 'sweetalert2';
import { useSidebarData } from '../../hooks/sidebarData';

const MyRequest = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { setSidebarData } = useSidebarData();

    const timeoutRef = useRef(null);

    const accesstoken = sessionStorage.getItem('accesstoken') || '';
    const aimtoken = sessionStorage.getItem('aimtoken') || '';
    const uname = sessionStorage.getItem('uname') || '';
    const name = sessionStorage.getItem('name') || '';
    const email = sessionStorage.getItem('email') || '';
    const level = sessionStorage.getItem('level') || '';
    const department = sessionStorage.getItem('department') || '';
    const departmentslug = sessionStorage.getItem('departmentslug') || '';
    const outlet = sessionStorage.getItem('outletname') || '';
    const outletcode = sessionStorage.getItem('outletcode') || '';
    
    const [currentPage, setCurrentPage] = useState(1);
    const [rowPerPage, setRowPerPage] = useState(10);
    
    const [formSubmit, setFormSubmit] = useState(false);
    const [formData, setFormData] = useState({
        id: 0,
        requesttype: '',
        startdate: '',
        starttime: '',
        enddate: '',
        endtime: '',
        totalhours: 0,
        justification: '',
        attachment: ''
    });

    const userNDOTDataKey = ['userNDOT', { uname, currentPage, rowPerPage }];

    const tableColumns = [
        { name: 'Request Type', selector: row => row.requesttype },
        { name: 'Start Date', selector: row => row.startdate },
        { name: 'End Date', selector: row => row.enddate },
        { name: 'Status', selector: row => row.status },
        { name: 'Action', selector: row => row.action }
    ];

    const handleInputChange = (e) => {
        const { name, value, files } = e.target;
        if (name === 'attachment[]') {
            setFormData({ ...formData, attachment: files }); 
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const resetForm = () => {
        setFormData({
            id: 0,
            requesttype: '',
            startdate: '',
            starttime: '',
            enddate: '',
            endtime: '',
            totalhours: 0,
            justification: '',
            attachment: ''
        });
        document.querySelector("input[name='attachment[]']").value = '';
    };

    const { data: requesttype, error: errorRequestType } = useQuery({
        queryKey: ['ndotselection'],
        queryFn: async () => {
            const res = await axios.get(`ndot/types`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data.response;
        },
        enabled: !!accesstoken,
        retry: 1
    });

    useEffect(() => {
        if (errorRequestType) {
            if (errorRequestType.response.data?.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    }, [errorRequestType]);

    const cancelNDOTRequestMutation = useMutation({
        mutationFn: async (ndotData) => {
            const res = await axios.put(`ndot`, ndotData, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data?.response;
        },
        onSuccess: (data) => {
            toast.fire({ icon: 'success', title: data });
            queryClient.invalidateQueries(userNDOTDataKey);
        },
        onError: (error) => {
            setFormSubmit(false);
            const errorMessage = error.response?.data?.response || 'Something went wrong while cancelling NDOT request!';
            toast.fire({ icon: 'error', title: errorMessage });
            if (error.response?.data.status === 'inactive') {
                destroySession();               
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    });

    const handleCancelNDOTRequest = (ndotid, date) => {
        Swal.fire({
            text: `Are you sure you want to cancel your NDOT request for ${date}?`,
            icon: 'warning',
            allowOutsideClick: false,
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            showLoaderOnConfirm: true,
            preConfirm: function(result) {
                return new Promise(function(resolve) {
                    if (result) {
                        cancelNDOTRequestMutation.mutate({
                            uname,
                            requestid: ndotid
                        });
                        resolve();
                    }  
                });
            }
        });
    }

    const handleViewNDOTRequest = (requesttype, ndotrequestid) => {
        setSidebarData('state', true);
        setSidebarData('key', 'ndot-request');
        setSidebarData('label', `${requesttype} Request Details`);
        setSidebarData('data', {
            uname,
            ndotrequestid: ndotrequestid
        });
    }

    const handleViewNDOTRequestStatus = (requesttype, requestid, sdate, edate) => {
        setSidebarData('state', true);
        setSidebarData('key', 'ndot-request-status');
        setSidebarData('label', `${requesttype} Request Status`);
        setSidebarData('data', {
            uname,
            ndotrequestid: requestid,
            sdate,
            edate
        });
    }
    
    const { data: userNDOT, isLoading: isLoadingUserNDOT, error: errorUserNDOT } = useQuery({
        queryKey: userNDOTDataKey,
        queryFn: async () => {
            const res = await axios.get(`ndot?uname=${uname}&page=${currentPage}&rows_per_page=${rowPerPage}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            const data = res.data.response?.userndot?.map(item => ({
                requesttype: item.requesttype,
                startdate: item.start_date,
                enddate: item.end_date,
                status: (
                        item.status === 1 
                            ? <span className="label label-primary" onClick={() => handleViewNDOTRequestStatus(item.requesttype, item.id, item.start_date, item.end_date)}>Pending</span> 
                            : (item.status === 2 
                                ? <span className="label label-success" onClick={() => handleViewNDOTRequestStatus(item.requesttype, item.id, item.start_date, item.end_date)}>Approved</span> 
                                : (item.status === 3 
                                    ? <span className="label label-danger" onClick={() => handleViewNDOTRequestStatus(item.requesttype, item.id, item.start_date, item.end_date)}>Disapproved</span> 
                                    : (item.status === 4 
                                        ? <span className="label label-warning" onClick={() => handleViewNDOTRequestStatus(item.requesttype, item.id, item.start_date, item.end_date)}>Cancelled</span> 
                                        : <span className="label label-primary" onClick={() => handleViewNDOTRequestStatus(item.requesttype, item.id, item.start_date, item.end_date)}>Pending</span>)))
                ),
                action: (
                    <div className="buttons">
                        <button type="button" className="btn-view" title="VIEW" onClick={() => handleViewNDOTRequest(item.requesttype, item.id)}>
                            <i className="fa fa-eye"></i>
                        </button>
                        { item.status !== 4 && (
                            <button type="button" className="btn-cancel" title="CANCEL" onClick={() => handleCancelNDOTRequest(item.id, item.start_date)}>
                                <i className="fa fa-times"></i>
                            </button>
                        )}
                    </div>
                )
            }));

            return { data, totalRows: res.data.response?.totalRows }
        },
        enabled: !!uname && !!currentPage && !!rowPerPage,
        retry: 1
    });

    const handlePageChange = (page) => {
        setCurrentPage(page);
    }

    const handleRowsPerPageChange = (newPerPage) => {
        setRowPerPage(newPerPage);
        setCurrentPage(1);
    };

    // Compact validation function
    const validateForm = () => {
        const requiredFields = ['requesttype', 'startdate', 'enddate', 'justification'];
        for (let field of requiredFields) {
            if (!formData[field]) {
                toast.fire({ icon: 'error', title: `${field.charAt(0).toUpperCase() + field.slice(1)} is required!` });
                return false;
            }
        }

        if (formData.enddate < formData.startdate) {
            toast.fire({ icon: 'error', title: 'End date cannot be earlier than Start date!' });
            return false;
        }

        // Time validation
        if (formData.starttime && formData.endtime) {
            const startDateTime = new Date(`${formData.startdate}T${formData.starttime}`);
            const endDateTime = new Date(`${formData.enddate}T${formData.endtime}`);

            if (startDateTime >= endDateTime) {
                toast.fire({ icon: 'error', title: 'End time must be later than Start time!' });
                return false;
            }
        }

        return true;
    };

    const userNDOTRequestMutation = useMutation({
        mutationFn: async (ndotRequestData) => {
            const res = await axios.post(`ndot`, ndotRequestData, {
                headers: { 
                    'Authorization': `Bearer ${accesstoken}`,
                    'X-Aim-Token': aimtoken,
                    'Content-Type': 'multipart/form-data' 
                }
            });
            return res.data.response;
        },
        onSuccess: (data) => {
            resetForm();
            setFormSubmit(false);
            queryClient.invalidateQueries(userNDOTDataKey);
            toast.fire({ icon: 'success', title: data });
        },
        onError: (error) => {
            setFormSubmit(false);
            const errorMessage = error.response?.data?.response || 'Something went wrong while sending NDOT request!';
            toast.fire({ icon: 'error', title: errorMessage });
            if (error.response?.data.status === 'inactive') {
                destroySession();               
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    });

    const handleSubmitNDOTRequest = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const formDataToSubmit = new FormData(e.target);
        
        // Add additional form data if necessary
        formDataToSubmit.append('uname', uname);
        formDataToSubmit.append('name', name);
        formDataToSubmit.append('email', email);
        formDataToSubmit.append('level', level);
        formDataToSubmit.append('department', department);
        formDataToSubmit.append('departmentcode', departmentslug);
        formDataToSubmit.append('outlet', outlet);
        formDataToSubmit.append('outletcode', outletcode);

        Swal.fire({
            text: `Are you sure you want to send this NDOT request?`,
            icon: 'warning',
            allowOutsideClick: false,
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            showLoaderOnConfirm: true,
            preConfirm: function(result) {
                return new Promise(function(resolve) {
                    if (result) {
                        setFormSubmit(true);
                        userNDOTRequestMutation.mutate(formDataToSubmit);
                        resolve();
                    }  
                });
            }
        });
    };

    return (
        <div className='my-request'>
            <div className="page-header">
                <div className='title'>
                    <h1>My Request</h1>
                    <ul className='breadcrumbs'>
                        <li><NavLink to='/nd-ot/my-request'>NDOT</NavLink></li>
                        <li><span>My Request</span></li>
                    </ul>
                </div>
            </div>

            <div className="panels">
                <div className="left-panel">
                    <div className="requests">
                        <DataTable 
                            columns={tableColumns} 
                            data={userNDOT?.data || []}
                            progressPending={isLoadingUserNDOT} 
                            progressComponent={<UserNDOTLoader />} 
                            pagination
                            paginationServer
                            paginationTotalRows={userNDOT?.totalRows || 0}
                            onChangeRowsPerPage={handleRowsPerPageChange}
                            onChangePage={handlePageChange}
                            dense
                            highlightOnHover
                            responsive
                        />
                    </div>
                </div>

                <div className="right-panel">
                    <form onSubmit={(e) => { handleSubmitNDOTRequest(e)}} encType="multipart/form-data">
                        <div className='form-group'>
                            <label>Request Type</label>
                            <select name='requesttype' value={formData.requesttype} onChange={handleInputChange} disabled={formSubmit}>
                                <option value=''></option>
                                {
                                    requesttype?.map(item => (
                                        <option key={item.id} value={item.id}>{item.requesttype}</option>
                                    ))
                                }
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Start</label>
                            <div className="date-group">
                                <div className="input-group">
                                    <div className='form-group'>
                                        <label>Date</label>
                                        <input type='date' name='startdate' value={formData.startdate} onChange={handleInputChange} disabled={formSubmit} />
                                    </div>
                                    <div className='form-group'>
                                        <label>Time</label>
                                        <input type='time' name='starttime' value={formData.starttime} onChange={handleInputChange} disabled={formSubmit} />
                                        <p className="note">leave empty if full day</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>End</label>
                            <div className="date-group">
                                <div className="input-group">
                                    <div className='form-group'>
                                        <label>Date</label>
                                        <input type='date' name='enddate' value={formData.enddate} min={formData.startdate} onChange={handleInputChange} disabled={formSubmit} />
                                    </div>
                                    <div className='form-group'>
                                        <label>Time</label>
                                        <input type='time' name='endtime' value={formData.endtime} onChange={handleInputChange} disabled={formSubmit} />
                                        <p className="note">leave empty if full day</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='form-group'>
                            <label>Justification</label>
                            <textarea rows="3" cols="5" name='justification' value={formData.justification} onChange={handleInputChange} disabled={formSubmit} />
                        </div>
                        <div className='form-group'>
                            <label>Attachment</label>
                            <input type='file' name='attachment[]' accept=".jpeg, .jpg, .png, .doc, .docx, .pdf" multiple onChange={handleInputChange} disabled={formSubmit} />
                        </div>
                        <div className='button-group'>
                            <button type='submit' disabled={formSubmit}>
                                {formSubmit ? <span>SAVING <i className="fa fa-spinner fa-spin"></i></span> : 'SAVE'}
                            </button>
                            <button type='button' onClick={resetForm} disabled={formSubmit}>RESET</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}

export default MyRequest;